import { Phone, Mail, MapPin, Clock, Building2 } from "lucide-react";
import { COMPANY } from "../lib/company";

const MAP_QUERY = encodeURIComponent(
  `${COMPANY.address.street}, ${COMPANY.address.zip} ${COMPANY.address.city}, Sweden`
);
const MAP_EMBED = `https://www.google.com/maps?q=${MAP_QUERY}&output=embed`;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`;

export default function Kontakt() {
  return (
    <div data-testid="page-kontakt">
      {/* Page Header */}
      <section
        className="bg-black text-white py-20 lg:py-28 relative overflow-hidden"
        data-testid="kontakt-header"
      >
        <div className="hero-grain" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12">
          <p className="overline text-[#FFD700] mb-5">Kontakta oss</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Det enklaste sättet?
            <br />
            <span className="text-[#FFD700]">Ring oss direkt.</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl leading-relaxed">
            Vi svarar snabbt, pratar rakt och kan ofta ge dig en prisuppgift
            redan i telefon.
          </p>
        </div>
      </section>

      {/* Massive Contact */}
      <section
        className="bg-white py-20 lg:py-28"
        data-testid="kontakt-main"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Phone & Email */}
            <div className="lg:col-span-7">
              <a
                href={COMPANY.phoneHref}
                className="block border-2 border-black p-8 lg:p-12 brut-shadow-yellow bg-white"
                data-testid="contact-phone-card"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Phone size={22} strokeWidth={2.5} />
                  <p className="overline">Telefon</p>
                </div>
                <p className="font-display font-black text-5xl sm:text-6xl lg:text-7xl text-black leading-[1] tracking-tighter">
                  {COMPANY.phone}
                </p>
                <p className="mt-4 text-gray-600 font-condensed uppercase tracking-wider">
                  Tryck för att ringa
                </p>
              </a>

              <a
                href={COMPANY.emailHref}
                className="mt-8 block border-2 border-black p-8 lg:p-12 brut-shadow bg-[#FFD700]"
                data-testid="contact-email-card"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Mail size={22} strokeWidth={2.5} />
                  <p className="overline">E-post</p>
                </div>
                <p className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-black leading-[1] tracking-tight break-all">
                  {COMPANY.email}
                </p>
                <p className="mt-4 text-black/70 font-condensed uppercase tracking-wider">
                  Skriv till oss
                </p>
              </a>
            </div>

            {/* Side info */}
            <div className="lg:col-span-5 space-y-6">
              <div
                className="border-2 border-black p-8"
                data-testid="contact-hours"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Clock size={20} strokeWidth={2.5} />
                  <p className="overline">Öppettider</p>
                </div>
                <ul className="space-y-2 text-gray-800 text-base">
                  <li className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="font-condensed uppercase tracking-wider">
                      Mån – Fre
                    </span>
                    <span className="font-bold">07:00 – 17:00</span>
                  </li>
                  <li className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="font-condensed uppercase tracking-wider">
                      Lördag
                    </span>
                    <span className="font-bold">Vid beställning</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="font-condensed uppercase tracking-wider">
                      Söndag
                    </span>
                    <span className="font-bold">Stängt</span>
                  </li>
                </ul>
                <p className="mt-4 text-sm text-gray-600">
                  Akuta uppdrag — ring för jour utanför kontorstid.
                </p>
              </div>

              <div
                className="border-2 border-black p-8 bg-[#111827] text-white"
                data-testid="contact-address-card"
              >
                <div className="flex items-center gap-3 mb-4">
                  <MapPin size={20} strokeWidth={2.5} color="#FFD700" />
                  <p className="overline text-[#FFD700]">Besöksadress</p>
                </div>
                <address className="not-italic font-display font-bold text-xl lg:text-2xl leading-tight">
                  {COMPANY.legal}
                  <br />
                  {COMPANY.address.street}
                  <br />
                  {COMPANY.address.zip} {COMPANY.address.city}
                </address>
                <a
                  href={MAP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 font-condensed uppercase tracking-wider text-sm font-bold text-[#FFD700] border-b-2 border-[#FFD700] pb-1 hover:text-white hover:border-white transition-colors"
                  data-testid="contact-map-link"
                >
                  Visa på karta
                </a>
              </div>

              <div
                className="border-2 border-black p-8"
                data-testid="contact-orgs"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Building2 size={20} strokeWidth={2.5} />
                  <p className="overline">Bolag</p>
                </div>
                <ul className="space-y-3 text-gray-800">
                  <li className="border-l-4 border-[#FFD700] pl-3">
                    <p className="font-display font-bold text-lg">
                      Humle Åkeri AB
                    </p>
                    <p className="text-sm text-gray-600 font-condensed uppercase tracking-wider">
                      Transport
                    </p>
                  </li>
                  <li className="border-l-4 border-[#166534] pl-3">
                    <p className="font-display font-bold text-lg">
                      Humle Entreprenad AB
                    </p>
                    <p className="text-sm text-gray-600 font-condensed uppercase tracking-wider">
                      Schakt & Mark
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section
        className="bg-[#F3F4F6] py-12 lg:py-16 border-t-2 border-black"
        data-testid="kontakt-map-section"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <p className="overline text-gray-500 mb-4">Hitta hit</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-black leading-tight mb-8">
            Rågstigen 19, Tungelsta
          </h2>
          <div className="border-2 border-black brut-shadow overflow-hidden bg-white">
            <iframe
              title="Karta — Humlegruppen Tungelsta"
              src={MAP_EMBED}
              className="w-full h-[420px] lg:h-[520px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
